import { useEffect, useState } from "react";
import { request } from "../services/server";

/**
 * Fetches koncern members for a workspace.
 * Returns { koncernInfo, isKoncern, loading }
 *
 * koncernInfo shape:
 * {
 *   parent: { orgnr, name },
 *   members: [{ orgnr, name }],
 *   all: [{ orgnr, name }],  // parent + members
 * }
 */
export default function useKoncernMembers(workspaceKey) {
  const [koncernInfo, setKoncernInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceKey) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await request(`/users/workspaces/${workspaceKey}/koncern/`);
        if (cancelled) return;

        // Backend returns `subsidiaries` (legacy field was `members`, kept
        // as a fallback in case older endpoints still use it).
        const raw = data?.subsidiaries || data?.members || [];
        const members = raw.map((m) => ({
          orgnr: m.orgnr || m.organisation?.organisationnummer || "",
          name: m.name || m.organisation?.namn || m.orgnr || "",
        }));

        if (members.length > 0) {
          const parent = {
            orgnr: data?.parent?.orgnr || workspaceKey,
            name: data?.parent?.name || data?.parent_name || workspaceKey,
          };
          setKoncernInfo({
            parent,
            members,
            all: [parent, ...members],
          });
        } else {
          setKoncernInfo(null);
        }
      } catch {
        if (!cancelled) setKoncernInfo(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [workspaceKey]);

  return {
    koncernInfo,
    isKoncern: !!koncernInfo && koncernInfo.all.length > 1,
    loading,
  };
}
